import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useLanguage } from '../../../contexts/LanguageContext';

/**
 * NSherjFh PR 6 — diary entry soft-delete confirm modal. Mirrors
 * PRs 2-5 modals. Copy reflects diary semantics: photos cascade via
 * parent, the unique (site, user, date) tuple frees up so the worker
 * can re-enter, and restoring brings everything back unless a new
 * entry already occupies the slot (in which case restore fails with
 * a unique-violation error — the admin resolves by soft-deleting
 * the newer entry first).
 */
const DiaryDeleteConfirmModal = ({ isOpen, onClose, onConfirm, entryLabel }) => {
  const { t } = useLanguage();
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-modal flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
      <div className="bg-card rounded-lg shadow-elevation-5 w-full max-w-md">
        <div className="p-6">
          <div className="flex items-start gap-4 mb-4">
            <div className="flex items-center justify-center w-12 h-12 bg-destructive/10 rounded-full flex-shrink-0">
              <Icon name="AlertTriangle" size={24} color="var(--color-destructive)" />
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-foreground mb-2">
                Delete diary entry?
              </h2>
              <p className="text-sm text-muted-foreground">
                Remove "{entryLabel}" from active views. You can restore it from the Deleted tab.
              </p>
            </div>
          </div>

          <div className="bg-warning/10 border border-warning/20 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <Icon name="AlertCircle" size={20} color="var(--color-warning)" className="flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-foreground mb-1">Photos + re-entry slot</p>
                <p className="text-sm text-muted-foreground">
                  Attached photos stay in storage. The (site, worker, date) slot becomes free again so the worker can re-enter for that day if needed. Restoring brings the entry and its photos back, unless a new entry already occupies the slot.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 p-4 border-t border-border bg-muted/30">
          <Button variant="outline" onClick={onClose}>
            {t('cancel')}
          </Button>
          <Button
            variant="destructive"
            onClick={onConfirm}
            iconName="Trash2"
            iconPosition="left"
          >
            Delete
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DiaryDeleteConfirmModal;
